import { useState, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { getTableConfig } from "../config/tableConfig";

export const useTableFilters = (tableType) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const config = getTableConfig(tableType);

  const initializeFilters = useCallback(() => {
    const filters = {};
    config?.urlParams.forEach((param) => {
      const value = searchParams.get(param);
      filters[param] = value || "";
    });
    return filters;
  }, [config?.urlParams, searchParams]);

  const [filters, setFilters] = useState(initializeFilters);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(0);

  const updateFilter = useCallback((filterKey, value) => {
    setFilters((prev) => ({
      ...prev,
      [filterKey]: value,
    }));
    setCurrentPage(0);
    updateSearchParams({ [filterKey]: value });
  }, []);

  const updateSearchParams = useCallback(
    (newFilters) => {
      const params = new URLSearchParams();
      params.set("tab", tableType);

      Object.entries({ ...filters, ...newFilters }).forEach(([key, value]) => {
        if (value !== "" && value !== null && value !== undefined) {
          params.set(key, value);
        }
      });

      setSearchParams(params);
    },
    [filters, tableType, setSearchParams]
  );

  // Clear all filters
  const clearFilters = useCallback(() => {
    setFilters(
      Object.fromEntries(config?.urlParams.map((param) => [param, ""]))
    );
    setCurrentPage(0);
    const params = new URLSearchParams();
    params.set("tab", tableType);
    setSearchParams(params);
  }, [config?.urlParams, tableType, setSearchParams]);

  // Get current API parameters from filters
  const apiParams = useMemo(() => {
    const params = {};
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== "" && value !== null && value !== undefined) {
        params[key] = value;
      }
    });
    return params;
  }, [filters]);

  // Pagination
  const currentOffset = currentPage * rowsPerPage;

  const handlePageChange = useCallback((event, newPage) => {
    setCurrentPage(newPage);
  }, []);

  const handleRowsPerPageChange = useCallback((event) => {
    setRowsPerPage(parseInt(event.target.value));
    setCurrentPage(0);
  }, []);

  return {
    // Filter state
    filters,
    updateFilter,
    clearFilters,
    apiParams,

    // Pagination state
    rowsPerPage,
    currentPage,
    currentOffset,
    handlePageChange,
    handleRowsPerPageChange,
    setCurrentPage,
    setRowsPerPage,

    // URL management
    updateSearchParams,
  };
};

export const useDynamicFilterOptions = (dataList, filterConfig) => {
  return useMemo(() => {
    if (!dataList || dataList.length === 0) {
      return {};
    }

    const options = {};

    filterConfig.forEach((filter) => {
      if (filter.type === "dynamic-select" && filter.dataKey) {
        const uniqueValues = [
          ...new Set(dataList.map((item) => item[filter.dataKey])),
        ];
        options[filter.id] = uniqueValues
          .filter((value) => value && value !== "--")
          .map((value) => ({
            label: value,
            value: value,
          }));
      }
    });

    return options;
  }, [dataList, filterConfig]);
};

export const useUrlFilters = (tableType) => {
  const [searchParams] = useSearchParams();
  const config = getTableConfig(tableType);

  return useMemo(() => {
    const filters = {};
    config.urlParams.forEach((param) => {
      const value = searchParams.get(param);
      filters[param] = value || "";
    });
    return filters;
  }, [searchParams, config.urlParams]);
};
